import { FormEvent, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';

import { getMyEmbedPack, getMySquad, listMatches, me, patchMe, type PortalMatch } from '../api/portal';
import { ApiError } from '../api/client';
import { ClubBrandingCard } from '../components/TeamBranding';
import { MatchupLabel, TeamCrest } from '../components/TeamCrest';
import { copyText } from '../lib/clipboard';
import { storySnippet } from '../lib/embedSnippets';
import { useT } from '../lib/i18n';
import { hasClips, hasOnAir, hasScouting, hasSocial, hasWire, isClubLogin } from '../lib/portalRole';

function isLive(status: string): boolean {
  return status.startsWith('in_play') || status === 'break' || status === 'suspended';
}

function isFinished(status: string): boolean {
  return status === 'finished' || status === 'abandoned' || status === 'cancelled';
}

function pickFocusMatch(rows: PortalMatch[]): PortalMatch | null {
  const live = rows.find((m) => isLive(m.status));
  if (live) return live;
  const upcoming = [...rows]
    .filter((m) => !isFinished(m.status) && !isLive(m.status))
    .sort((a, b) => new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime());
  if (upcoming[0]) return upcoming[0];
  const ft = [...rows]
    .filter((m) => m.status === 'finished')
    .sort((a, b) => new Date(b.scheduled_at).getTime() - new Date(a.scheduled_at).getTime());
  return ft[0] ?? null;
}

export function ClubHomePage() {
  const { t } = useT();
  const profile = useQuery({ queryKey: ['portal-me'], queryFn: me });
  const matches = useQuery({ queryKey: ['portal-matches'], queryFn: listMatches, refetchInterval: 20_000 });
  const pack = useQuery({
    queryKey: ['portal-my-embed-pack'],
    queryFn: getMyEmbedPack,
    enabled: isClubLogin(profile.data),
  });
  const squad = useQuery({
    queryKey: ['portal-squad'],
    queryFn: getMySquad,
    enabled: isClubLogin(profile.data),
  });

  const focus = useMemo(() => pickFocusMatch(matches.data ?? []), [matches.data]);
  const [copied, setCopied] = useState<'pack' | 'story' | 'calendar' | null>(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [pwMsg, setPwMsg] = useState<string | null>(null);
  const [pwErr, setPwErr] = useState<string | null>(null);

  const pw = useMutation({
    mutationFn: () => {
      if (newPassword !== confirm) throw new ApiError(400, 'mismatch', t('home.pwMismatch'));
      return patchMe({ current_password: currentPassword, new_password: newPassword });
    },
    onSuccess: () => {
      setCurrentPassword('');
      setNewPassword('');
      setConfirm('');
      setPwErr(null);
      setPwMsg(t('home.pwSaved'));
    },
    onError: (e) => {
      setPwMsg(null);
      setPwErr(e instanceof ApiError ? e.message : String(e));
    },
  });

  if (profile.data && !isClubLogin(profile.data)) {
    return <Navigate to="/matches" replace />;
  }

  const copy = async (kind: 'pack' | 'story' | 'calendar', text: string) => {
    await copyText(text);
    setCopied(kind);
    setTimeout(() => setCopied(null), 1500);
  };

  const kickoff = focus?.scheduled_at
    ? new Date(focus.scheduled_at).toLocaleString('sr-RS', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
    : '';
  const heading = focus
    ? isLive(focus.status) ? t('home.liveNow') : isFinished(focus.status) ? t('home.lastFt') : t('home.next')
    : t('home.next');

  return (
    <div>
      <div className="page-head">
        <div className="page-kicker">{t('home.kicker')}</div>
        <h1 className="page-title">
          <span className="inline-flex items-center gap-3">
            <TeamCrest logoUrl={pack.data?.team_logo_url} size={40} />
            {t('home.title')}
          </span>
        </h1>
        <p className="page-sub">{t('home.sub')}</p>
      </div>

      {profile.data?.parent_company_name && (
        <div className="card mb-4 p-3 text-sm text-slate-300">
          {t('home.league')}: <span className="text-white">{profile.data.parent_company_name}</span>
          {(profile.data.allowed_teams ?? []).length > 0 && (
            <> · {(profile.data.allowed_teams ?? []).map((x) => x.name).join(', ')}</>
          )}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="card p-4">
          <div className="text-[11px] uppercase tracking-wide text-slate-500">{heading}</div>
          {focus ? (
            <>
              <div className="mt-1 text-lg font-semibold text-white">
                <MatchupLabel home={focus.home} away={focus.away} homeLogo={focus.home_logo_url} awayLogo={focus.away_logo_url} size={28} className="gap-2" />
              </div>
              <div className="mt-1 text-sm text-slate-400">
                {kickoff}
                {isFinished(focus.status) || isLive(focus.status)
                  ? ` · ${focus.score_home}–${focus.score_away}`
                  : ''}
                {focus.round_name ? ` · ${focus.round_name}` : ''}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Link to={`/matches/${focus.id}/report`} className="btn-secondary text-xs">{t('home.zapisnik')}</Link>
                {hasClips(profile.data) && isFinished(focus.status) && (
                  <Link to={`/videos?create=1&match=${focus.id}`} className="btn-primary text-xs">{t('home.highlight')}</Link>
                )}
                <button
                  type="button"
                  className="btn-ghost text-xs"
                  onClick={() => copy('story', storySnippet(focus.id))}
                >
                  {copied === 'story' ? t('home.copied') : t('home.copyStory')}
                </button>
                <Link to="/matches" className="btn-ghost text-xs">{t('home.openMatches')}</Link>
                {hasWire(profile.data) && <Link to="/wire" className="btn-ghost text-xs">Wire</Link>}
                {hasOnAir(profile.data) && <Link to="/onair" className="btn-ghost text-xs">On-Air</Link>}
                {hasSocial(profile.data) && <Link to="/social" className="btn-ghost text-xs">Social desk</Link>}
                {hasScouting(profile.data) && <Link to="/scouting" className="btn-ghost text-xs">Scouting</Link>}
              </div>
            </>
          ) : (
            <div className="mt-2 text-sm text-slate-500">
              {t('home.noMatch')}{' '}
              <Link to="/matches" className="text-white underline-offset-2 hover:underline">{t('home.openMatches')}</Link>
            </div>
          )}
        </div>

        <div className="card p-4">
          <div className="text-[11px] uppercase tracking-wide text-slate-500">{t('home.share')}</div>
          <p className="mt-1 text-[12px] text-slate-500">{t('home.shareSub')}</p>
          <pre className="mt-2 overflow-auto text-[11px] text-slate-300">
            {focus ? storySnippet(focus.id) : '<!-- MATCH_ID -->'}
          </pre>
        </div>
      </div>

      <div className="card mt-4 p-4">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-slate-500">{t('home.embedTitle')}</div>
            <p className="mt-1 text-[12px] text-slate-500">{t('home.embedSub')}</p>
          </div>
          <button
            type="button"
            className="btn-primary text-xs"
            disabled={!pack.data?.html}
            onClick={() => pack.data && copy('pack', pack.data.html)}
          >
            {copied === 'pack' ? t('home.copied') : t('home.copyPack')}
          </button>
        </div>
        <pre className="max-h-64 overflow-auto text-[11px] text-slate-300 whitespace-pre-wrap">
          {pack.data?.html ?? (pack.isError ? String(pack.error) : '…')}
        </pre>
        {pack.data?.calendar_url && (
          <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-white/5 pt-3 text-[12px] text-slate-400">
            <span>{t('home.calendar')}</span>
            <code className="truncate text-slate-300">{pack.data.calendar_url}</code>
            <button
              type="button"
              className="btn-ghost text-xs"
              onClick={() => pack.data?.calendar_url && copy('calendar', pack.data.calendar_url)}
            >
              {copied === 'calendar' ? t('home.copied') : t('home.copyLink')}
            </button>
          </div>
        )}
      </div>

      <ClubBrandingCard />

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <div className="card p-4">
          <div className="text-[11px] uppercase tracking-wide text-slate-500">{t('home.squad')}</div>
          {(squad.data?.players ?? []).length === 0 ? (
            <p className="mt-2 text-sm text-slate-500">{t('home.squadEmpty')}</p>
          ) : (
            <table className="table mt-2">
              <thead>
                <tr>
                  <th>{t('home.shirt')}</th>
                  <th>{t('home.player')}</th>
                  <th>{t('home.role')}</th>
                </tr>
              </thead>
              <tbody>
                {(squad.data?.players ?? []).map((p) => (
                  <tr key={p.person_id}>
                    <td className="tabular-nums text-slate-400">{p.shirt_number ?? '—'}</td>
                    <td className="text-white">
                      {p.photo_url && (
                        <img src={p.photo_url} alt="" className="mr-2 inline-block h-6 w-6 rounded-full object-cover align-middle" />
                      )}
                      {p.full_name}
                      {p.is_captain ? <span className="ml-1 text-[11px] text-slate-500">({t('home.captain')})</span> : null}
                    </td>
                    <td className="text-xs text-slate-400">{p.role}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <form
          className="card p-4"
          onSubmit={(e: FormEvent) => { e.preventDefault(); pw.mutate(); }}
        >
          <div className="text-[11px] uppercase tracking-wide text-slate-500">{t('home.password')}</div>
          <p className="mt-1 mb-3 text-[12px] text-slate-500">{t('home.passwordSub')}</p>
          <label className="label">{t('home.currentPw')}</label>
          <input className="input mb-2" type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required />
          <label className="label">{t('home.newPw')}</label>
          <input className="input mb-2" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} minLength={8} required />
          <label className="label">{t('home.confirmPw')}</label>
          <input className="input mb-3" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} minLength={8} required />
          {pwMsg && <div className="mb-2 text-sm text-emerald-300">{pwMsg}</div>}
          {pwErr && <div className="mb-2 text-sm text-rose-300">{pwErr}</div>}
          <button type="submit" className="btn-primary" disabled={pw.isPending}>
            {pw.isPending ? t('home.saving') : t('home.savePw')}
          </button>
        </form>
      </div>
    </div>
  );
}
